import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import UltimaNotificacionWidget from './UltimaNotificacionWidget';
import SemestresWidget from './SemestresWidget';
import HistorialAcademicoEstudianteWidget from './HistorialAcademicoEstudianteWidget';

import MiClaseHoyWidget from './MiClaseHoyWidget';
import ReunionesSalaDocenteWidget from './ReunionesSalaDocenteWidget';
import TareasProximasWidget from './TareasProximasWidget';
import TareasWidgetCuadrado from './TareasWidgetCuadrado';

// import EmailWidgetCuadrado removed — module deleted (was zombie module)
import VersiculoWidgetCuadrado from './VersiculoWidgetCuadrado';
import CumpleanosWidgetCuadrado from './CumpleanosWidgetCuadrado';
import PruebasExamenesWidgetCuadrado from './PruebasExamenesWidgetCuadrado';
import ProfesoresCapacitacionWidget from './ProfesoresCapacitacionWidget';
import MiCodigoReferidoWidget from './MiCodigoReferidoWidget';
import ReferidosPendientesWidget from './ReferidosPendientesWidget';
import EstudiantesPendientesWidget from './EstudiantesPendientesWidget';
import GraduadosPendientesWidget from './GraduadosPendientesWidget';
import ProximaClaseWidget from './ProximaClaseWidget';
import ProximasClasesAdminWidget from './ProximasClasesAdminWidget';
import CompromisoPagoWidget from './CompromisoPagoWidget';
import CompromisosPagoAdminWidget from './CompromisosPagoAdminWidget';
import FormularioRegistroPendienteWidget from './FormularioRegistroPendienteWidget';
import AnuncioDashboardWidget from './AnuncioDashboardWidget';
import InstallPWAWidget from './InstallPWAWidget';
import PruebasSinCorregirWidget from './PruebasSinCorregirWidget';
import BannerPruebasPendientes from './BannerPruebasPendientes';
import SoporteButton from './SoporteButton';
import BannerAvisoCuotas from './BannerAvisoCuotas';
import UsersOnlineWidget from './UsersOnlineWidget';

import axios from 'axios';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, API, getImageUrl } = useContext(AuthContext);
  const [stats, setStats] = useState({});
  const [recentClasses, setRecentClasses] = useState([]);
  const [recentNews, setRecentNews] = useState([]);
  const [proximosEventos, setProximosEventos] = useState([]);
  const [proximasActividades, setProximasActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  // (unreadEmails state removed — emails internos & correspondencia modules deleted from IBA Connect)
  const [backupReminder, setBackupReminder] = useState(null);
  const [cumpleaneros, setCumpleaneros] = useState([]);
  const [denarios, setDenarios] = useState(null);
  const [solicitudesBecasPendientes, setSolicitudesBecasPendientes] = useState(0);
  const [comprobantesPendientes, setComprobantesPendientes] = useState(0);
  const [verseOfTheDay, setVerseOfTheDay] = useState(null);
  
  // Estados para "Mis Clases Inscritas"
  const [misClasesInscritas, setMisClasesInscritas] = useState([]);
  const [semestreSeleccionado, setSemestreSeleccionado] = useState(null);
  const [marcandoAsistencia, setMarcandoAsistencia] = useState({});
  const [semestresInfo, setSemestresInfo] = useState({});
  const [semestresData, setSemestresData] = useState({});
  const [calificaciones, setCalificaciones] = useState([]);
  
  // Estado para clases del profesor
  const [clasesProfesor, setClasesProfesor] = useState([]);

  useEffect(() => {
    // Si es profesor NO capacitado (explícitamente false), redirigir a capacitación
    // Si capacitado es undefined/null/true → acceso normal
    if (user && user.rol === 'profesor' && user.capacitado === false) {
      navigate('/app/clases');
      return;
    }
    
    // Cargar todos los datos del dashboard (incluye calificaciones y clases)
    loadDashboardData();
    loadBackupReminder();
    loadCumpleaneros();
    loadSolicitudesBecasPendientes();
    loadComprobantesPendientes();
    loadVerseOfTheDay();
    registrarActividadDiaria();
    
    // Actualización automática cada 30 segundos
    const interval = setInterval(() => {
      loadSolicitudesBecasPendientes();
      loadComprobantesPendientes();
      
      // Recargar calificaciones para estudiantes (actualización día a día)
      if (['estudiante', 'pastor', 'graduado'].includes(user?.rol)) {
        loadCalificaciones();
      }
      
      // Recargar clases para profesores/admin (actualización de progreso)
      if (user?.rol === 'profesor' || user?.rol === 'admin') {
        loadClasesProfesor();
      }
    }, 30000);
    
    // Recargar cuando el usuario regresa a la aplicación (focus)
    const handleFocus = () => {
      if (['estudiante', 'pastor', 'graduado'].includes(user?.rol)) {
        loadCalificaciones();
      }
      if (user?.rol === 'profesor' || user?.rol === 'admin') {
        loadClasesProfesor();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    // Load denarios for all users
    if (user) {
      loadDenarios();
    }
  }, [user]);

  // (loadUnreadEmails removed — emails internos module deleted from IBA Connect)
  
  // Función para formatear hora de clase (UTC a Montevideo)
  const formatClaseHora = (fechaInicio) => {
    if (!fechaInicio) return '';
    
    try {
      // Si la fecha NO tiene Z al final, agregarla para que se interprete como UTC
      let dateStr = fechaInicio;
      if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
        dateStr = dateStr + 'Z';
      }
      
      // Parsear la fecha UTC que viene del backend
      const utcDate = new Date(dateStr);
      
      // Convertir a hora de Montevideo (UTC-3)
      const montevideoTime = new Date(utcDate.getTime() - (3 * 60 * 60 * 1000));
      
      // Formatear la hora en formato 12 horas con AM/PM
      const hours = montevideoTime.getUTCHours();
      const minutes = montevideoTime.getUTCMinutes();
      
      const hours12 = hours % 12 || 12; // Convertir a formato 12 horas
      const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
      const minutesStr = String(minutes).padStart(2, '0');
      
      return `${hours12}:${minutesStr} ${ampm}`;
    } catch (error) {
      console.error('Error formatting clase hora:', error);
      return '';
    }
  };

  const loadBackupReminder = async () => {
    // Solo para administradores
    if (user?.rol !== 'admin') return;
    
    try {
      const response = await axios.get(`${API}/admin/backup-reminder`);
      if (response.data.show_reminder) {
        setBackupReminder(response.data);
      }
    } catch (error) {
      console.log('Error loading backup reminder:', error);
    }
  };

  const loadSolicitudesBecasPendientes = async () => {
    // Solo para administradores
    if (user?.rol !== 'admin') return;
    
    try {
      const response = await axios.get(`${API}/becas/solicitudes/pendientes/count`);
      setSolicitudesBecasPendientes(response.data.count || 0);
    } catch (error) {
      console.log('Error loading solicitudes pendientes:', error);
    }
  };

  const loadDenarios = async () => {
    try {
      const response = await axios.get(`${API}/denarios/mi-balance`);
      console.log('Denarios cargados:', response.data); // Debug
      setDenarios(response.data);
    } catch (error) {
      console.error('Error loading denarios:', error);
      // Set default values if error
      setDenarios({
        denarios_totales: 0,
        nivel_actual: 'Sembrador',
        nivel_icono: '🌱'
      });
    }
  };

  const registrarActividadDiaria = async () => {
    try {
      const response = await axios.post(`${API}/denarios/actividad-diaria`);
      if (response.data.denarios_otorgados) {
        // Show toast notification about daily denarios
        toast.success(response.data.mensaje);
        // Reload denarios to show updated balance for all users
        if (user) {
          loadDenarios();
        }
      }
    } catch (error) {
      // Silent fail - don't bother the user
      console.log('Error registrando actividad diaria:', error);
    }
  };

  const loadComprobantesPendientes = async () => {
    // Solo para administradores
    if (user?.rol !== 'admin') return;
    
    try {
      const response = await axios.get(`${API}/admin/registraciones/pendientes`);
      const pendientes = response.data?.registraciones_pendientes || [];
      setComprobantesPendientes(pendientes.length);
    } catch (error) {
      console.error('Error loading comprobantes pendientes:', error);
    }
  };


  // Función helper para hacer requests con retry
  const fetchWithRetry = async (url, retries = 2, delay = 1000) => {
    for (let i = 0; i <= retries; i++) {
      try {
        const response = await axios.get(url);
        return { success: true, data: response.data };
      } catch (error) {
        if (i === retries) {
          console.warn(`⚠️ Failed after ${retries + 1} attempts: ${url}`);
          return { success: false, data: null, error };
        }
        // Esperar antes de reintentar
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  const loadDashboardData = async () => {
    try {
      const timestamp = Date.now();
      
      // Usar Promise.allSettled para que fallas individuales no bloqueen todo
      const results = await Promise.allSettled([
        fetchWithRetry(`${API}/dashboard/stats?_t=${timestamp}`),
        fetchWithRetry(`${API}/clases?_t=${timestamp}`),
        fetchWithRetry(`${API}/noticias?_t=${timestamp}`),
        fetchWithRetry(`${API}/dashboard/proximos-eventos?_t=${timestamp}`),
        fetchWithRetry(`${API}/clases/por-semestre?_t=${timestamp}`),
        fetchWithRetry(`${API}/actividades-extracurriculares?_t=${timestamp}`)
      ]);

      // Procesar resultados individualmente - si uno falla, los demás siguen
      const [statsResult, classesResult, newsResult, eventosResult, semestresResult, actividadesResult] = results;

      if (statsResult.status === 'fulfilled' && statsResult.value?.success) {
        setStats(statsResult.value.data);
      }
      
      let classesData = [];
      if (classesResult.status === 'fulfilled' && classesResult.value?.success) {
        classesData = classesResult.value.data || [];
        setRecentClasses(classesData.slice(0, 3));
      }
      
      if (newsResult.status === 'fulfilled' && newsResult.value?.success) {
        setRecentNews((newsResult.value.data || []).slice(0, 3));
      }
      
      if (eventosResult.status === 'fulfilled' && eventosResult.value?.success) {
        setProximosEventos(eventosResult.value.data?.eventos || []);
      }
      
      if (semestresResult.status === 'fulfilled' && semestresResult.value?.success) {
        setSemestresInfo(semestresResult.value.data?.semestres_info || {});
        setSemestresData(semestresResult.value.data?.semestres || {});
      }
      
      // Procesar actividades extracurriculares - filtrar solo activas y futuras
      if (actividadesResult.status === 'fulfilled' && actividadesResult.value?.success) {
        const actividadesData = actividadesResult.value.data || [];
        if (Array.isArray(actividadesData)) {
          const ahora = new Date();
          const actividadesFuturas = actividadesData
            .filter(a => a.estado === 'activo' && new Date(a.fecha_inicio) >= ahora)
            .sort((a, b) => new Date(a.fecha_inicio) - new Date(b.fecha_inicio))
            .slice(0, 5);
          setProximasActividades(actividadesFuturas);
        }
      }
      
      // Si es estudiante o graduado, cargar mis clases inscritas
      if (user?.rol === 'estudiante' || user?.rol === 'graduado' || user?.rol === 'pastor') {
        await loadMisClasesInscritas(classesData);
        await loadCalificaciones();
      }
      
      // Si es profesor o admin, cargar sus clases
      if (user?.rol === 'profesor' || user?.rol === 'admin') {
        await loadClasesProfesor();
      }
      
      // Solo mostrar error si TODAS las llamadas fallaron
      const allFailed = results.every(r => r.status === 'rejected' || !r.value?.success);
      if (allFailed) {
        toast.error('Error al cargar datos del dashboard. Intenta recargar la página.');
      }
    } catch (error) {
      console.error('Error al cargar datos del dashboard:', error);
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadMisClasesInscritas = async (todasLasClases) => {
    try {
      // Filtrar solo las clases donde el estudiante está inscrito
      const clasesInscritas = todasLasClases.filter(clase => 
        clase.estudiantes_inscritos && clase.estudiantes_inscritos.includes(user.id)
      );
      
      setMisClasesInscritas(clasesInscritas);
      
      // Establecer el primer semestre disponible como seleccionado por defecto
      if (clasesInscritas.length > 0) {
        const semestres = [...new Set(clasesInscritas.flatMap(c => c.semestres || []))].sort((a, b) => a - b);
        if (semestres.length > 0 && !semestreSeleccionado) {
          setSemestreSeleccionado(semestres[0]);
        }
      }
    } catch (error) {
      console.error('Error al procesar clases inscritas:', error);
    }
  };

  const loadCalificaciones = async () => {
    try {
      const response = await axios.get(`${API}/calificaciones`);
      setCalificaciones(response.data || []);
    } catch (error) {
      console.error('Error al cargar calificaciones:', error);
    }
  };

  const calcularPromediosPorClase = () => {
    if (!calificaciones || calificaciones.length === 0) return [];
    
    // Filtrar calificaciones por semestre seleccionado si aplica
    let calificacionesFiltradas = calificaciones;
    if (semestreSeleccionado) {
      calificacionesFiltradas = calificaciones.filter(cal => {
        const clase = misClasesInscritas.find(c => c.id === cal.clase_id);
        return clase && clase.semestres && clase.semestres.includes(semestreSeleccionado);
      });
    }
    
    // Agrupar por clase
    const clasesMap = {};
    calificacionesFiltradas.forEach(cal => {
      if (!clasesMap[cal.clase_id]) {
        clasesMap[cal.clase_id] = {
          clase_id: cal.clase_id,
          clase_titulo: cal.clase_titulo || 'Clase sin título',
          notas: []
        };
      }
      // Solo agregar notas numéricas válidas
      const nota = parseFloat(cal.nota || cal.calificacion);
      if (!isNaN(nota)) {
        clasesMap[cal.clase_id].notas.push(nota);
      }
    });
    
    // Calcular promedio por clase y preparar datos para la gráfica
    const promedios = Object.values(clasesMap)
      .filter(clase => clase.notas.length > 0) // Solo clases con calificaciones
      .map(clase => {
        const promedio = clase.notas.reduce((sum, nota) => sum + nota, 0) / clase.notas.length;
        return {
          name: clase.clase_titulo,
          value: Math.round(promedio * 10) / 10, // Redondear a 1 decimal
          count: clase.notas.length
        };
      });
    
    return promedios;
  };

  const calcularPromedioGeneral = (promedios) => {
    // Calcular promedio de PROMEDIOS por clase (método correcto)
    if (!promedios || promedios.length === 0) return 0;
    const suma = promedios.reduce((acc, p) => acc + p.value, 0);
    return Math.round((suma / promedios.length) * 10) / 10;
  };

  const loadClasesProfesor = async () => {
    try {
      // Agregar timestamp para evitar cache del navegador
      const response = await axios.get(`${API}/clases?_t=${Date.now()}`);
      // Filtrar solo las clases del profesor actual
      const misClases = response.data.filter(clase => clase.profesor_id === user.id);
      setClasesProfesor(misClases);
    } catch (error) {
      console.error('Error al cargar clases del profesor:', error);
    }
  };

  const calcularProgresoClases = () => {
    if (!clasesProfesor || clasesProfesor.length === 0) return [];
    
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const clasesActivas = clasesProfesor.filter(clase => {
      if (clase.activa === false) return false;
      if (!clase.fecha_inicio || !clase.fecha_fin) return false;
      
      const fechaInicio = new Date(clase.fecha_inicio);
      fechaInicio.setHours(0, 0, 0, 0);
      
      const fechaFin = new Date(clase.fecha_fin);
      fechaFin.setHours(23, 59, 59, 999);
      
      return fechaInicio <= hoy && hoy <= fechaFin;
    });
    
    const progreso = clasesActivas.map(clase => {
      const fechaInicio = new Date(clase.fecha_inicio);
      fechaInicio.setHours(0, 0, 0, 0);
      
      const fechaFin = new Date(clase.fecha_fin);
      fechaFin.setHours(23, 59, 59, 999);
      
      const totalDias = Math.ceil((fechaFin - fechaInicio) / (1000 * 60 * 60 * 24));
      const diasTranscurridos = Math.max(0, Math.ceil((hoy - fechaInicio) / (1000 * 60 * 60 * 24)));
      const diasRestantes = Math.max(0, Math.ceil((fechaFin - hoy) / (1000 * 60 * 60 * 24)));
      
      let porcentajeTranscurrido = 0;
      if (totalDias > 0) {
        porcentajeTranscurrido = Math.min(100, Math.max(0, (diasTranscurridos / totalDias) * 100));
      }
      
      const porcentajeRestante = 100 - porcentajeTranscurrido;
      
      return {
        id: clase.id,
        nombre: clase.titulo || 'Sin título',
        porcentajeTranscurrido: Math.round(porcentajeTranscurrido),
        porcentajeRestante: Math.round(porcentajeRestante),
        diasRestantes,
        totalDias,
        fechaInicio: fechaInicio.toLocaleDateString('es-UY'),
        fechaFin: fechaFin.toLocaleDateString('es-UY')
      };
    });
    
    return progreso.sort((a, b) => a.nombre.localeCompare(b.nombre));
  };

  // Handlers para marcar presente
  const marcarAsistencia = async (claseId) => {
    setMarcandoAsistencia(prev => ({ ...prev, [claseId]: true }));
    
    try {
      await axios.post(`${API}/asistencias/marcar`, {
        clase_id: claseId,
        presente: true
      });
      
      toast.success('Asistencia marcada como presente exitosamente');
    } catch (error) {
      console.error('Error marking attendance:', error);
      toast.error('Error al marcar asistencia');
    } finally {
      setMarcandoAsistencia(prev => ({ ...prev, [claseId]: false }));
    }
  };

  const ordenarDiasSemana = (dias) => {
    const ordenDias = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
    return dias.sort((a, b) => ordenDias.indexOf(a) - ordenDias.indexOf(b));
  };

  const getSemestreNombre = (numero) => {
    const info = semestresInfo[numero];
    if (info && typeof info === 'object') {
      return info.nombre || `Semestre ${numero}`;
    }
    return `Semestre ${numero}`;
  };

  const ordenarClasesPorDia = (clases) => {
    const ordenDias = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
    
    return [...clases].sort((a, b) => {
      // Si una clase no tiene días, va al final
      if (!a.dias_semana || a.dias_semana.length === 0) return 1;
      if (!b.dias_semana || b.dias_semana.length === 0) return -1;
      
      // Obtener el primer día de cada clase según el orden de la semana
      const primerDiaA = Math.min(...a.dias_semana.map(dia => ordenDias.indexOf(dia)));
      const primerDiaB = Math.min(...b.dias_semana.map(dia => ordenDias.indexOf(dia)));
      
      return primerDiaA - primerDiaB;
    });
  };

  const getDiaHoyEspanol = () => {
    const diasSemanaMap = {
      'sunday': 'domingo',
      'monday': 'lunes', 
      'tuesday': 'martes',
      'wednesday': 'miércoles',
      'thursday': 'jueves',
      'friday': 'viernes',
      'saturday': 'sábado'
    };
    
    const hoyUTC = new Date();
    const hoyMontevideo = new Date(hoyUTC.getTime() - (3 * 60 * 60 * 1000));
    const diaHoyIngles = hoyMontevideo.toLocaleDateString('en', { weekday: 'long' }).toLowerCase();
    return diasSemanaMap[diaHoyIngles];
  };

  const esClaseDeHoy = (clase) => {
    const hoyUTC = new Date();
    const hoyMontevideo = new Date(hoyUTC.getTime() - (3 * 60 * 60 * 1000));
    
    const fechaInicio = new Date(clase.fecha_inicio);
    const fechaFin = new Date(clase.fecha_fin);
    
    if (hoyMontevideo < fechaInicio || hoyMontevideo > fechaFin) {
      return false;
    }
    
    const diaHoy = getDiaHoyEspanol();
    return clase.dias_semana && clase.dias_semana.includes(diaHoy);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const loadCumpleaneros = async () => {
    try {
      const response = await axios.get(`${API}/users/cumpleanos-hoy`);
      setCumpleaneros(response.data.cumpleaneros || []);
    } catch (error) {
      console.error('Error al cargar cumpleaños:', error);
    }
  };

  const loadVerseOfTheDay = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/bible/verse-of-the-day`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerseOfTheDay(response.data.verse);
    } catch (error) {
      console.error('Error loading verse of the day:', error);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Mañana';
    if (diffDays <= 7) return `En ${diffDays} días`;
    
    return date.toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getStatsCards = () => {
    if (user.rol === 'admin') {
      return [
        { title: 'Total Usuarios', value: stats.total_usuarios || 0, icon: 'fas fa-users', color: 'bg-blue-500', link: '/app/gestion-usuarios' },
        { title: 'Total Clases', value: stats.total_clases || 0, icon: 'fas fa-chalkboard', color: 'bg-green-500', link: '/app/clases' },
        { title: 'Estudiantes', value: stats.total_estudiantes || 0, icon: 'fas fa-user-graduate', color: 'bg-purple-500', link: '/app/gestion-usuarios?filtro=estudiante' },
        { title: 'Profesores', value: stats.total_profesores || 0, icon: 'fas fa-chalkboard-teacher', color: 'bg-orange-500', link: '/app/gestion-usuarios?filtro=profesor' },
      ];
    } else if (user.rol === 'profesor') {
      return [
        { title: 'Mis Clases', value: stats.mis_clases || 0, icon: 'fas fa-chalkboard', color: 'bg-blue-500', link: '/app/clases' },
        { title: 'Tareas Pendientes', value: stats.tareas_pendientes || 0, icon: 'fas fa-tasks', color: 'bg-orange-500', link: '/app/centro-evaluaciones' },
        { title: 'Estudiantes Total', value: stats.estudiantes_total || 0, icon: 'fas fa-user-graduate', color: 'bg-green-500', link: '/app/clases' },
      ];
    } else {
      return [
        { title: 'Clases Inscritas', value: stats.clases_inscritas || 0, icon: 'fas fa-book-open', color: 'bg-blue-500', link: '/app/clases' },
        { title: 'Tareas Pendientes', value: stats.tareas_pendientes || 0, icon: 'fas fa-clock', color: 'bg-orange-500', link: '/app/centro-evaluaciones' },
        { title: 'Calificaciones', value: stats.calificaciones_totales || 0, icon: 'fas fa-star', color: 'bg-green-500', link: '/app/calificaciones' },
      ];
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const esEstudiante = ['estudiante', 'pastor', 'graduado'].includes(user?.rol);

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {getGreeting()}, {user.nombre}!
          </h1>
          <p className="text-gray-600">
            Bienvenido a tu panel del Instituto Bíblico Apostólico del Uruguay
          </p>
        </div>
        <div className="mt-4 lg:mt-0 flex items-center gap-3">
          <Badge variant="secondary" className="text-sm px-4 py-2 bg-blue-100 text-blue-800">
            <i className="fas fa-user-circle mr-2"></i>
            {user.rol.charAt(0).toUpperCase() + user.rol.slice(1)}
          </Badge>
        </div>
      </div>

      {/* 📅 Próximas Reuniones Sala Docente — Prioridad TOP para profesores/admin (mobile-first) */}
      <ReunionesSalaDocenteWidget user={user} />

      {/* Widget de Compromiso de Pago - Estudiantes (prioritario, se muestra arriba) */}
      <div className="mb-6">
        <CompromisoPagoWidget />
      </div>

      {/* Banner de Aviso de Cuotas - Estudiantes */}
      <BannerAvisoCuotas />

      {/* Widget de Formulario de Registro Pendiente - Estudiantes */}
      <div className="mb-6">
        <FormularioRegistroPendienteWidget user={user} />
      </div>

      {/* Widget Instalación PWA — permanente para todos los usuarios */}
      <div className="mb-6">
        <InstallPWAWidget />
      </div>

      {/* Banner de Pruebas/Exámenes Pendientes - Estudiantes */}
      <BannerPruebasPendientes />

      {/* Grid de Widgets Cuadrados Bonitos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6 auto-rows-[14rem]">
        {/* 🔔 Última Notificación Push pendiente — primero en grid (1ro en mobile,
            1er cuadrado en desktop). Se renderiza solo si hay no-leídas. */}
        <UltimaNotificacionWidget />

        {/* Pruebas Sin Corregir - Profesores/Admin (Posición 1) */}
        <PruebasSinCorregirWidget />
        {/* Anuncio del Dashboard - Configurable por Admin */}
        <AnuncioDashboardWidget />
        {/* Compromisos de Pago - Solo Admin */}
        <CompromisosPagoAdminWidget />

        {/* Próxima Clase con Countdown - Estudiantes y Profesores (capacitación) */}
        <ProximaClaseWidget />

        {/* Próximas Clases con Countdown - Solo Admin */}
        <ProximasClasesAdminWidget />

        {/* Tareas - Solo si hay tareas urgentes */}
        <TareasWidgetCuadrado />


        {/* Pruebas y Exámenes - Solo si hay pruebas pendientes */}
        <PruebasExamenesWidgetCuadrado />

        {/* Profesores con Capacitación Pendiente - Solo Admin */}
        <ProfesoresCapacitacionWidget />

        {/* Usuarios Online - REMOVIDO, reemplazado por OnlinePresenceBar en header */}

        {/* Referidos Pendientes - Solo Admin */}
        <ReferidosPendientesWidget />

        {/* Estudiantes Pendientes (formulario) - Solo Admin */}
        <EstudiantesPendientesWidget />

        {/* Graduados/Pastores Pendientes de Aprobación - Solo Admin */}
        <GraduadosPendientesWidget />

        {/* Versículo del Día - SIEMPRE */}
        <VersiculoWidgetCuadrado />

        {/* Cumpleaños del Día - Widget Cuadrado con foto y melodía */}
        <CumpleanosWidgetCuadrado />

        {/* Mi Código de Referido - Estudiantes, pastores y profesores */}
        {(user?.rol === 'estudiante' || user?.rol === 'pastor' || user?.rol === 'profesor') && (
          <MiCodigoReferidoWidget />
        )}
      </div>

      {/* Widget: Mis Clases del Semestre - Para TODOS los roles */}
      <MiClaseHoyWidget />

      {/* Cumpleaños del Día - MOVIDO A WIDGET CUADRADO EN LA GRILLA SUPERIOR */}

      {/* Banner de Solicitudes de Becas Pendientes - Solo Admin */}
      {user?.rol === 'admin' && solicitudesBecasPendientes > 0 && (
        <Card className="border-l-4 border-green-500 bg-gradient-to-r from-green-50 via-blue-50 to-purple-50 shadow-xl overflow-hidden">
          <CardContent className="p-0">
            <div className="flex flex-col sm:flex-row items-center justify-between p-6 gap-4">
              {/* Ícono y Contenido */}
              <div className="flex items-center space-x-4 w-full sm:w-auto">
                {/* Ícono con gradiente similar al email */}
                <div className="relative w-20 h-20 flex-shrink-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-400 via-blue-500 to-purple-600 rounded-full opacity-20 blur-lg"></div>
                  <div className="relative w-20 h-20 bg-gradient-to-br from-green-500 via-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-4xl">🎓</span>
                  </div>
                </div>
                
                {/* Texto */}
                <div className="flex-1">
                  <h3 className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 text-xl mb-1">
                    {solicitudesBecasPendientes === 1 
                      ? '¡Nueva Solicitud de Beca!' 
                      : `¡${solicitudesBecasPendientes} Solicitudes de Beca Pendientes!`}
                  </h3>
                  <p className="text-gray-700 font-medium flex items-center">
                    <i className="fas fa-clock mr-2 text-blue-500"></i>
                    {solicitudesBecasPendientes === 1 
                      ? 'Hay 1 estudiante esperando aprobación' 
                      : `Hay ${solicitudesBecasPendientes} estudiantes esperando aprobación`}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Revisa y gestiona las solicitudes de becas
                  </p>
                </div>
              </div>

              {/* Botón */}
              <Button 
                className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 hover:from-green-700 hover:via-blue-700 hover:to-purple-700 text-white font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center gap-2 w-full sm:w-auto justify-center whitespace-nowrap"
                asChild
              >
                <a href="/app/solicitudes-becas">
                  <i className="fas fa-clipboard-check"></i>
                  Ver Solicitudes
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backup Reminder Alert - Fin de Mes */}
      {backupReminder && backupReminder.show_reminder && (
        <Card className="border-red-300 bg-gradient-to-r from-red-50 to-orange-50 shadow-lg animate-pulse">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                  <i className="fas fa-database text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-red-800 text-lg">
                    {backupReminder.message}
                  </h3>
                  <p className="text-sm text-red-600 font-medium">
                    ⚠️ Guarda tu backup mensual para proteger tus datos
                  </p>
                </div>
              </div>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white font-bold px-6" 
                asChild
              >
                <a href="/app/gestion-backups">
                  <i className="fas fa-download mr-2"></i>
                  Hacer Backup
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {getStatsCards().map((stat, index) => (
          <Card 
            key={index} 
            className="card-hover border-0 shadow-md cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
            onClick={() => navigate(stat.link)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-full ${stat.color} flex items-center justify-center`}>
                  <i className={`${stat.icon} text-white text-lg`}></i>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-400 flex items-center">
                <i className="fas fa-arrow-right mr-1"></i>
                Click para ver más
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Denarios Widget - Para todos los usuarios */}
        {user && denarios && (
          <Card className="card-hover border-0 shadow-md cursor-pointer" onClick={() => window.location.href = '/app/mis-denarios'}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Mis Denarios</p>
                  <p className="text-3xl font-bold text-gray-900">{denarios.denarios_totales}</p>
                  <p className="text-xs text-gray-500 mt-1">{denarios.nivel_icono} {denarios.nivel_actual}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center">
                  <i className="fas fa-coins text-white text-lg"></i>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Gráfica de Promedio del Semestre - Solo para Estudiantes */}
      {['estudiante', 'pastor', 'graduado'].includes(user?.rol) && (() => {
        const promedios = calcularPromediosPorClase();
        const promedioGeneral = calcularPromedioGeneral(promedios);
        const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
        
        if (promedios.length === 0) return null;
        
        return (
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center text-2xl">
                <i className="fas fa-chart-pie text-blue-600 mr-3"></i>
                Promedio de Clases {semestreSeleccionado ? `- ${getSemestreNombre(semestreSeleccionado)}` : ''}
              </CardTitle>
              
              {/* Selector de Semestre */}
              {misClasesInscritas.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    variant={!semestreSeleccionado ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSemestreSeleccionado(null)}
                    className={!semestreSeleccionado ? "instituto-gradient text-white" : ""}
                  >
                    Todos los semestres
                  </Button>
                  {[...new Set(misClasesInscritas.flatMap(c => c.semestres || []))].sort((a, b) => a - b).map(sem => (
                    <Button
                      key={sem}
                      variant={semestreSeleccionado === sem ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSemestreSeleccionado(sem)}
                      className={semestreSeleccionado === sem ? "instituto-gradient text-white" : ""}
                    >
                      {getSemestreNombre(sem)}
                    </Button>
                  ))}
                </div>
              )}
            </CardHeader>
            
            <CardContent>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                  {/* Gráfica */}
                  <div className="relative" style={{ width: 280, height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={promedios}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={110}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {promedios.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                                  <p className="font-semibold text-gray-800">{payload[0].name}</p>
                                  <p className="text-blue-600 font-bold text-lg">{payload[0].value}</p>
                                  <p className="text-xs text-gray-500">{payload[0].payload.count} calificaciones</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    
                    {/* Promedio en el centro */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                      <div className="text-4xl font-bold text-blue-600">
                        {promedioGeneral}
                      </div>
                      <div className="text-xs text-gray-600 font-medium mt-1">
                        Promedio General
                      </div>
                    </div>
                  </div>
                  
                  {/* Leyenda */}
                  <div className="flex flex-col gap-2">
                    {promedios.map((entry, index) => (
                      <div key={index} className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow-sm">
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{entry.name}</p>
                          <p className="text-xs text-gray-500">{entry.count} calificaciones</p>
                        </div>
                        <div className="text-lg font-bold" style={{ color: COLORS[index % COLORS.length] }}>
                          {entry.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Mis Clases de Hoy - Solo para Estudiantes y solo si hay clases hoy */}
      {['estudiante', 'pastor', 'graduado'].includes(user?.rol) && misClasesInscritas.filter(c => {
        const cumpleSemestre = !semestreSeleccionado || (c.semestres && c.semestres.includes(semestreSeleccionado));
        return cumpleSemestre && esClaseDeHoy(c);
      }).length > 0 && (
        <Card className="shadow-lg border-0">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-2xl">
                <i className="fas fa-calendar-day text-blue-600 mr-3"></i>
                Mis Clases de Hoy
              </CardTitle>
              {misClasesInscritas.length > 0 && (
                <Badge className="bg-blue-100 text-blue-800 text-sm">
                  {misClasesInscritas.filter(c => {
                    const cumpleSemestre = !semestreSeleccionado || (c.semestres && c.semestres.includes(semestreSeleccionado));
                    return cumpleSemestre && esClaseDeHoy(c);
                  }).length} {misClasesInscritas.filter(c => {
                    const cumpleSemestre = !semestreSeleccionado || (c.semestres && c.semestres.includes(semestreSeleccionado));
                    return cumpleSemestre && esClaseDeHoy(c);
                  }).length === 1 ? 'clase hoy' : 'clases hoy'}
                </Badge>
              )}
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {misClasesInscritas
                .filter(clase => {
                  // Filtrar por semestre seleccionado
                  const cumpleSemestre = !semestreSeleccionado || (clase.semestres && clase.semestres.includes(semestreSeleccionado));
                  // Filtrar solo clases del día de hoy
                  const esDiaHoy = esClaseDeHoy(clase);
                  return cumpleSemestre && esDiaHoy;
                })
                .map((clase) => (
                <div
                  key={clase.id}
                  className="group relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 hover:border-blue-400"
                  onClick={() => window.location.href = '/app/clases'}
                >
                  {/* Banner o fondo de color */}
                  <div className="h-32 w-full overflow-hidden relative bg-gradient-to-br from-blue-500 to-purple-600">
                    {clase.banner_url || clase.imagen_url ? (
                      <img 
                        src={clase.banner_url 
                          ? (clase.banner_url.startsWith('http') ? clase.banner_url : getImageUrl(clase.banner_url))
                          : getImageUrl(clase.imagen_url)
                        } 
                        alt={clase.titulo}
                        className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <i className="fas fa-book-open text-white text-4xl opacity-50"></i>
                      </div>
                    )}
                    
                    {/* Indicador si la clase es hoy */}
                    {esClaseDeHoy(clase) && (
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-green-500 text-white text-xs font-semibold animate-pulse">
                          <i className="fas fa-circle mr-1"></i>
                          Hoy
                        </Badge>
                      </div>
                    )}
                  </div>
                  
                  {/* Información de la clase */}
                  <div className="p-4 bg-white">
                    <h3 className="font-bold text-gray-900 text-sm mb-2 line-clamp-2 min-h-[2.5rem]">
                      {clase.titulo}
                    </h3>
                    
                    {/* Badge del Semestre - Movido aquí */}
                    {clase.semestres && clase.semestres.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {clase.semestres.map(sem => (
                          <Badge key={sem} className="bg-blue-100 text-blue-700 text-xs font-semibold">
                            <i className="fas fa-graduation-cap mr-1"></i>
                            {getSemestreNombre(sem)}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Información del profesor */}
                    <div className="flex items-center space-x-2 mb-3">
                      {clase.profesor_foto ? (
                        <img
                          src={getImageUrl(clase.profesor_foto)}
                          alt="Profesor"
                          className="w-8 h-8 rounded-full object-cover border-2 border-blue-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                          {clase.profesor_nombre ? clase.profesor_nombre.split(' ').map(n => n[0]).join('') : '?'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate">
                          {clase.profesor_nombre || 'Sin profesor'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Días de la semana con hora de inicio */}
                    <div className="mb-3">
                      {clase.dias_semana && clase.dias_semana.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {ordenarDiasSemana([...clase.dias_semana]).slice(0, 3).map((dia) => (
                            <Badge key={dia} variant="secondary" className="text-xs bg-blue-100 text-blue-700 font-semibold">
                              {dia.substring(0, 3).toUpperCase()}
                            </Badge>
                          ))}
                          {clase.dias_semana.length > 3 && (
                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 font-semibold">
                              +{clase.dias_semana.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Hora de inicio de la clase */}
                      {clase.fecha_inicio && (
                        <div className="flex items-center text-xs text-gray-600 mt-2">
                          <i className="fas fa-clock mr-2 text-blue-500"></i>
                          <span className="font-semibold">
                            {formatClaseHora(clase.fecha_inicio)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {misClasesInscritas.filter(c => {
              const cumpleSemestre = !semestreSeleccionado || (c.semestres && c.semestres.includes(semestreSeleccionado));
              return cumpleSemestre && esClaseDeHoy(c);
            }).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <i className="fas fa-calendar-times text-4xl mb-3"></i>
                <p className="font-medium">No tienes clases hoy</p>
                <p className="text-sm mt-1">
                  {getDiaHoyEspanol().charAt(0).toUpperCase() + getDiaHoyEspanol().slice(1)} - {new Date().toLocaleDateString('es-UY', { day: 'numeric', month: 'long' })}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lista de Clases del Semestre Seleccionado - Oculto para estudiantes (duplica MiClaseHoyWidget) */}
      {!esEstudiante && ['estudiante', 'pastor', 'graduado'].includes(user?.rol) && semestreSeleccionado !== null && (
        <Card className="shadow-lg border-0">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-xl">
              <i className="fas fa-list text-blue-600 mr-3"></i>
              Clases de {getSemestreNombre(semestreSeleccionado)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const semestreKey = `semestre_${semestreSeleccionado}`;
              const clasesDelSemestre = semestresData[semestreKey]?.clases || [];
              
              if (clasesDelSemestre.length === 0) {
                return (
                  <div className="text-center py-8">
                    <i className="fas fa-book text-gray-300 text-4xl mb-4"></i>
                    <p className="text-gray-500">No hay clases en este semestre</p>
                  </div>
                );
              }
              
              return (
                <div className="space-y-3">
                  {clasesDelSemestre.map((clase, idx) => (
                    <div 
                      key={clase.id || idx} 
                      className="flex items-start space-x-3 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 hover:shadow-md transition-all"
                    >
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-book-open text-white"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {clase.titulo}
                        </h4>
                        {clase.profesor_nombre && (
                          <p className="text-xs text-gray-600 mt-1">
                            <i className="fas fa-user-tie mr-1"></i>
                            {clase.profesor_nombre}
                          </p>
                        )}
                        {clase.estudiantes_inscritos > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            <i className="fas fa-users mr-1"></i>
                            {clase.estudiantes_inscritos} estudiantes inscritos
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {clase.categoria === 'estudiantes' ? 'Estudiantes' : 'Profesores'}
                      </Badge>
                    </div>
                  ))}
                  <div className="text-center pt-2 border-t border-blue-200">
                    <p className="text-sm font-semibold text-blue-800">
                      Total: {clasesDelSemestre.length} {clasesDelSemestre.length === 1 ? 'clase' : 'clases'}
                    </p>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Semestres Widget - Full width. Para estudiantes se muestra el historial académico personal (aprobadas/no aprobadas/pendientes). Admin/profesor conserva la vista con conteos. */}
      {esEstudiante ? <HistorialAcademicoEstudianteWidget /> : <SemestresWidget />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Classes - Oculto para estudiantes (zona 7) */}
        {!esEstudiante && (
        <Card className="shadow-lg border-0">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-xl">
              <i className="fas fa-chalkboard-teacher text-blue-600 mr-3"></i>
              Clases Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentClasses.length > 0 ? (
              <div className="space-y-4">
                {recentClasses.map((clase) => (
                  <a 
                    key={clase.id} 
                    href={`/clase/${clase.id}`}
                    className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer block"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-book text-blue-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">
                        {clase.titulo}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Profesor: {clase.profesor_nombre}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(clase.fecha_inicio)}
                      </p>
                    </div>
                  </a>
                ))}
                <Button variant="outline" className="w-full mt-4 btn-hover" asChild>
                  <a href="/app/clases">
                    Ver todas las clases
                    <i className="fas fa-arrow-right ml-2"></i>
                  </a>
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="fas fa-chalkboard text-gray-300 text-4xl mb-4"></i>
                <p className="text-gray-500">No hay clases recientes</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Próximos Eventos - Oculto para estudiantes (zona 7) */}
        {!esEstudiante && (
        <Card className="shadow-lg border-0">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-xl">
              <i className="fas fa-calendar-check text-green-600 mr-3"></i>
              Próximos Eventos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {proximosEventos.length > 0 ? (
              <div className="space-y-4">
                {proximosEventos.slice(0, 4).map((evento, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className={`w-10 h-10 bg-${evento.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <i className={`${evento.icono} text-${evento.color}-600`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">
                        {evento.titulo}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {evento.descripcion}
                      </p>
                      {evento.fecha && (
                        <div className="flex items-center mt-2">
                          <Badge variant="outline" className="text-xs">
                            <i className="fas fa-clock mr-1"></i>
                            {formatDateShort(evento.fecha)}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {proximosEventos.length === 0 && (
                  <div className="text-center py-8">
                    <i className="fas fa-calendar-check text-gray-300 text-4xl mb-4"></i>
                    <p className="text-gray-500">No hay eventos próximos</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="fas fa-calendar-check text-gray-300 text-4xl mb-4"></i>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Todo al día</h4>
                <p className="text-gray-500">No tienes eventos próximos pendientes</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Widget Próximas Actividades Extracurriculares */}
        {proximasActividades.length > 0 && (
          <Card className="shadow-lg border-0 bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center text-xl">
                <i className="fas fa-star text-emerald-500 mr-3"></i>
                Próximas Actividades
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {proximasActividades.map((actividad) => {
                  const fecha = new Date(actividad.fecha_inicio);
                  const tipoLabels = {
                    'viaje': { icon: 'fas fa-bus', color: 'blue' },
                    'taller': { icon: 'fas fa-tools', color: 'purple' },
                    'retiro': { icon: 'fas fa-mountain', color: 'amber' },
                    'conferencia': { icon: 'fas fa-microphone', color: 'cyan' },
                    'visita': { icon: 'fas fa-walking', color: 'pink' },
                    'actividad': { icon: 'fas fa-star', color: 'emerald' }
                  };
                  const tipoInfo = tipoLabels[actividad.tipo] || tipoLabels['actividad'];
                  
                  return (
                    <div 
                      key={actividad.id} 
                      className="flex items-start space-x-3 p-3 rounded-lg bg-white border border-emerald-100 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => navigate('/app/actividades-extra')}
                    >
                      <div className={`w-10 h-10 bg-${tipoInfo.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <i className={`${tipoInfo.icon} text-${tipoInfo.color}-600`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          ⭐ {actividad.titulo}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                          {actividad.descripcion}
                        </p>
                        <div className="flex items-center mt-2 space-x-2">
                          <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                            <i className="fas fa-calendar mr-1"></i>
                            {fecha.toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </Badge>
                          {actividad.ubicacion && (
                            <Badge variant="outline" className="text-xs">
                              <i className="fas fa-map-marker-alt mr-1"></i>
                              {actividad.ubicacion.length > 15 ? actividad.ubicacion.substring(0, 15) + '...' : actividad.ubicacion}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Button 
                  variant="outline" 
                  className="w-full mt-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  onClick={() => navigate('/app/actividades-extra')}
                >
                  <i className="fas fa-arrow-right mr-2"></i>
                  Ver todas las actividades
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
        {/* Recent News - Oculto para estudiantes (zona 9) */}
        {!esEstudiante && (
        <Card className="shadow-lg border-0">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-xl">
              <i className="fas fa-newspaper text-green-600 mr-3"></i>
              Noticias del Instituto
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentNews.length > 0 ? (
              <div className="space-y-4">
                {recentNews.map((noticia) => (
                  <div key={noticia.id} className="p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      {noticia.titulo}
                    </h4>
                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                      {noticia.contenido.substring(0, 100)}...
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Por: {noticia.autor_nombre}</span>
                      <span>{formatDate(noticia.fecha_publicacion)}</span>
                    </div>
                  </div>
                ))}
                <Button variant="outline" className="w-full mt-4 btn-hover" asChild>
                  <a href="/app/noticias">
                    Ver todas las noticias
                    <i className="fas fa-arrow-right ml-2"></i>
                  </a>
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="fas fa-newspaper text-gray-300 text-4xl mb-4"></i>
                <p className="text-gray-500">No hay noticias recientes</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* Quick Actions */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center text-xl">
            <i className="fas fa-bolt text-yellow-500 mr-3"></i>
            Acciones Rápidas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button variant="outline" className="h-20 flex-col space-y-2 btn-hover" asChild>
              <a href="/app/calendario">
                <i className="fas fa-calendar-alt text-blue-600 text-xl"></i>
                <span className="text-sm">Calendario</span>
              </a>
            </Button>
            <Button variant="outline" className="h-20 flex-col space-y-2 btn-hover" asChild>
              <a href="/app/calificaciones">
                <i className="fas fa-chart-bar text-purple-600 text-xl"></i>
                <span className="text-sm">Calificaciones</span>
              </a>
            </Button>
            <Button variant="outline" className="h-20 flex-col space-y-2 btn-hover" asChild>
              <a href="/app/tareas">
                <i className="fas fa-tasks text-orange-600 text-xl"></i>
                <span className="text-sm">Tareas</span>
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default Dashboard;
